import { tgimFetch } from './httpClient';

export type FeatureFlagState = 'implemented' | 'stub' | 'hidden';

/** Mirrors Android `TgImFeatureFlagsRegistryKeys` / backend `registry.json`. */
export const TGIM_FEATURE = {
  AUTH_QR: 'tg_im_auth_qr',
  AUTH_EMAIL: 'tg_im_auth_email',
  MSG_SEND_TEXT: 'tg_im_msg_send_text',
  MSG_SEND_MEDIA: 'tg_im_msg_send_media',
  MSG_SEND_VOICE: 'tg_im_msg_send_voice',
  MSG_SEND_VIDEO: 'tg_im_msg_send_video',
  GROUPS: 'tg_im_groups',
  FRIENDS: 'tg_im_friends',
  CHANNELS: 'tg_im_channels',
  STICKERS_MARKET: 'tg_im_stickers_market',
  CALLS: 'tg_im_calls',
  PROFILE_USERNAME: 'tg_im_profile_username',
} as const;

let cachedFlags: Record<string, FeatureFlagState> = {};
const shownStubKeys = new Set<string>();
// `cachedFlags` is a plain module variable, not global state, so a component reading it directly
// in render (e.g. an auth-gate button) never re-renders once `loadFeatureFlags` resolves after
// first paint. Subscribers here let such components opt into a re-render on that one event.
const flagsLoadedSubscribers = new Set<() => void>();

type FeatureFlagsResponse = { version: number; flags: Record<string, FeatureFlagState> };

export async function loadFeatureFlags(): Promise<Record<string, FeatureFlagState>> {
  const response = await tgimFetch<FeatureFlagsResponse>('/feature-flags?flatten=1');
  cachedFlags = response.flags;
  flagsLoadedSubscribers.forEach((cb) => cb());
  return cachedFlags;
}

/** Notifies once per resolved `loadFeatureFlags` call (including ones already in flight). */
export function subscribeToFeatureFlagsLoaded(cb: () => void): () => void {
  flagsLoadedSubscribers.add(cb);
  return () => flagsLoadedSubscribers.delete(cb);
}

export function getFeatureFlagState(key: string): FeatureFlagState {
  return cachedFlags[key] ?? 'implemented';
}

export function isFeatureHidden(key: string): boolean {
  return getFeatureFlagState(key) === 'hidden';
}

export function isFeatureStub(key: string): boolean {
  return getFeatureFlagState(key) === 'stub';
}

type GuardFeatureActions = { showNotification: (payload: { message: string }) => void };

const FEATURE_LABEL_ZH: Record<string, string> = {
  [TGIM_FEATURE.AUTH_QR]: '扫码登录',
  [TGIM_FEATURE.AUTH_EMAIL]: '邮箱登录',
  [TGIM_FEATURE.MSG_SEND_TEXT]: '发送文字',
  [TGIM_FEATURE.MSG_SEND_MEDIA]: '发送图片/文件',
  [TGIM_FEATURE.MSG_SEND_VOICE]: '发送语音',
  [TGIM_FEATURE.MSG_SEND_VIDEO]: '发送视频',
  [TGIM_FEATURE.GROUPS]: '群组',
  [TGIM_FEATURE.FRIENDS]: '好友',
  [TGIM_FEATURE.CHANNELS]: '频道',
  [TGIM_FEATURE.STICKERS_MARKET]: '贴纸市场',
  [TGIM_FEATURE.CALLS]: '通话',
  [TGIM_FEATURE.PROFILE_USERNAME]: '用户名',
};

/** Returns true when the caller should abort (stub/hidden). Matches Android toast-on-block. */
export function guardFeature(key: string, actions: GuardFeatureActions): boolean {
  const state = getFeatureFlagState(key);
  const label = FEATURE_LABEL_ZH[key] || '该功能';

  if (state === 'hidden') {
    actions.showNotification({ message: `${label}暂不支持` });
    return true;
  }

  if (state === 'stub') {
    if (!shownStubKeys.has(key)) {
      shownStubKeys.add(key);
      actions.showNotification({ message: `${label}暂未开放，敬请期待` });
    }
    return true;
  }

  return false;
}

export function startFeatureFlagsFocusPoll(): () => void {
  const handler = () => {
    void loadFeatureFlags();
  };
  window.addEventListener('focus', handler);
  return () => window.removeEventListener('focus', handler);
}

/** iOS `TgImFeatureFlagsStore.clear` — drop cache on logout so auth gates re-fetch cleanly. */
export function clearFeatureFlagsCache(): void {
  cachedFlags = {};
  shownStubKeys.clear();
}
